import { useEffect, useState } from 'react'
import PageHero from '@/components/ui/PageHero'
import ArchiveGrid from '@/components/ui/ArchiveGrid'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useSEO } from '@/hooks/useSEO'
import type { Archive } from '@/types'
import * as api from '@/lib/api'

export default function ArchivesPage() {
  // SEO
  useSEO({
    title: 'The Archives',
    description: 'Browse the twelve archives of The Archive Network — civilizations, conflicts, treaties, lineages, explorations, classified secrets, mythology, origins, creeds, frauds, disasters, and philosophy.',
    url: 'https://thearchivenetwork.com/archives',
  })

  const [loading, setLoading] = useState(true)
  const [archives, setArchives] = useState<Archive[]>([])

  useEffect(() => {
    async function loadData() {
      try {
        const data = await api.getArchives()
        setArchives(data)
      } catch (error) {
        console.error('Failed to load archives:', error)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="animate-fade-in">
      <PageHero
        image="/images/covers/archives.jpg"
        title="The Archives"
        subtitle="A growing network of specialized archives, each documenting a distinct domain of human history. Every archive stands alone yet interconnects with the others."
        variant="full"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <ArchiveGrid archives={archives} />

        {/* Archive descriptions */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {archives.map((archive) => (
            <div key={archive.key} className="artifact-frame p-6">
              <h3 className="font-display text-xl text-archive-cream mb-3">
                {archive.name}
              </h3>
              <p className="text-archive-muted text-sm leading-relaxed">
                {archive.description}
              </p>
              <a
                href={archive.site_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-4 text-sm text-archive-gold hover:text-archive-amber transition-colors"
              >
                Visit Archive Site
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
